export interface DocParam {
  name: string;
  type: string;
  required?: boolean;
  description: string;
}

export interface DocEndpoint {
  id: string;
  legacyId?: string;
  method: "GET" | "POST" | "PATCH";
  path: string;
  summary: string;
  description: string[];
  queryParams?: DocParam[];
  bodyParams?: DocParam[];
  responseParams?: DocParam[];
  requestExample?: string;
  responseExample?: string;
}

export interface DocResource {
  id: string;
  title: string;
  description: string[];
  fields?: DocParam[];
  endpoints: DocEndpoint[];
}

const BASE_URL = "https://sandbox.api.veltiz.com/v0";

export const exampleIds = {
  holder: "151b3f2a91cc8e1d4b6f9a3c5d7e0f2b8c4a",
  account: "7e1b72c4d9e18f3a4c5b8d6e2f0a9b1c3d5e",
  investor: "5e9eb82f6d31a4c94e7f8b2d5a0c6e9f1b3d",
  portfolio: "18534e91c2d7b6a84f3e9c1d5b7a2f8e0c4d",
  externalTrade: "8db1c57d20a94b8e4f6c9d2a7b3e5f0c8d1a",
  instrumentRequest: "7cda7c44e19b2d8f4a6c8e3b5d9f1a2c4e6b",
  equityInstrument: "1740a10c93f25b7d4e8f9a1c3b5d7e9f0a2c",
  currencyInstrument: "09c05d8e2f7a9c1b4d6e8f0a3c5b7d9e1f2a",
  debtInstrument: "790e9e3c5a7d1f2b4c6e8d0a2b4c6d8e0f1a",
  integration: "9a426f1d8c3b5a7e4f9c2d0b8a6e4c2f0d8b",
  role: "14732a5c8e0b3d6f4a7c9e1b5d8f0a3c6e9b",
  attachment: "2b7f4c1a9e3d5b8f0a2c4e6d8b0f1a3c5e7d",
  positionParseRequest: "9c41a7e5b3d1f8a2c4e6b0d2f4a6c8e0b2d4",
  tradeParseRequest: "ba53f7dfcf6099da11d5adab4241c4e401af",
  positionSnapshot: "3a05c9e1b7d4f2a6c8e0b2d4f6a8c0e2b4d6",
};

interface CurlOptions {
  method: "GET" | "POST" | "PATCH";
  path: string;
  scopeType?: "holder" | "account";
  body?: string;
  contentType?: string;
  dataBinary?: string;
}

export function curl({
  method,
  path,
  scopeType,
  body,
  contentType,
  dataBinary,
}: CurlOptions): string {
  const lines = [
    method === "GET"
      ? `curl "${BASE_URL}${path}" \\`
      : `curl -X ${method} "${BASE_URL}${path}" \\`,
    `  -H "Content-Type: ${contentType ?? "application/json"}" \\`,
    `  -H "User-Id: $INTEGRATION_ID" \\`,
    `  -H "User-Type: integration" \\`,
    `  -H "Access-Time: $ACCESS_TIME" \\`,
    `  -H "Access-Signature: $ACCESS_SIGNATURE"`,
  ];
  if (scopeType) {
    lines[lines.length - 1] += " \\";
    lines.push(`  -H "Scope-Type: ${scopeType}" \\`);
    lines.push(
      `  -H "Scope-Id: ${scopeType === "holder" ? exampleIds.holder : exampleIds.account}"`
    );
  }
  if (body) {
    lines[lines.length - 1] += " \\";
    lines.push(`  -d '${body}'`);
  }
  if (dataBinary) {
    lines[lines.length - 1] += " \\";
    lines.push(`  --data-binary @${dataBinary}`);
  }
  return lines.join("\n");
}

const paginationParams: DocParam[] = [
  {
    name: "limit",
    type: "integer",
    description: "Number of items to return, up to 100. Defaults to 100.",
  },
  {
    name: "cursor",
    type: "string",
    description:
      "Opaque cursor returned by a previous page. Omit it for the first page.",
  },
];

const instrumentListParams: DocParam[] = [
  ...paginationParams,
  {
    name: "ids",
    type: "list of strings",
    description: "Comma-separated instrument ids, up to 100.",
  },
  {
    name: "status",
    type: "string",
    description: "Filter by instrument status.",
  },
  {
    name: "after",
    type: "string",
    description: "Return instruments created after this date or timestamp.",
  },
  {
    name: "before",
    type: "string",
    description: "Return instruments created before this date or timestamp.",
  },
  {
    name: "include_fields",
    type: "list of strings",
    description: "Extra derived fields to include, such as `price_amount`.",
  },
];

const parseRequestResultsFieldParam: DocParam = {
  name: "include_fields",
  type: "list of strings",
  description:
    "Comma-separated extra fields to include; `results` is the only value read today, and an unrecognized value embeds nothing rather than failing.",
};

const parseRequestListParams: DocParam[] = [
  ...paginationParams,
  {
    name: "status",
    type: "string",
    description: "Filter by a single status.",
  },
  {
    name: "attachment_id",
    type: "string",
    description: "Filter by attachment.",
  },
  {
    name: "portfolio_id",
    type: "string",
    description: "Filter by portfolio.",
  },
  {
    name: "unique_id",
    type: "string",
    description: "Find the request carrying this duplicate guard.",
  },
  parseRequestResultsFieldParam,
];

export const resources: DocResource[] = [
  {
    id: "holders",
    title: "Holders",
    description: [
      "A Holder is your organization on the platform — a family office, an asset manager, or a fund administrator. It is the root of the scope hierarchy: accounts belong to a holder, and users, integrations, and roles are granted inside it.",
      "Holders are created by Veltiz during onboarding, so the API only lists them. Use this endpoint to discover the holder id your credential is bound to — you will send it as `Scope-Id` on holder-scoped requests, such as instrument requests.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique holder identifier." },
      { name: "name", type: "string", description: "Legal name of the organization." },
      {
        name: "status",
        type: "string",
        description: "One of `active`, `blocked`, `frozen`, or `closed`.",
      },
      { name: "tax_id", type: "string", description: "Tax identifier of the organization (CNPJ)." },
      {
        name: "representative_tax_ids",
        type: "list of strings",
        description: "Tax identifiers of the legal representatives.",
      },
      {
        name: "group_ids",
        type: "list of strings",
        description: "Identifiers of the groups the holder belongs to.",
      },
      { name: "tags", type: "list of strings", description: "Free-form labels attached to the holder." },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "list-holders",
        method: "GET",
        path: "/account-holder",
        summary: "List Holders",
        description: [
          "Returns the holders your credential is bound to. With the `Scope-Type: holder` headers set, the response is limited to that single holder.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "status",
            type: "string",
            description: "Filter by status: `active`, `blocked`, `frozen`, or `closed`.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/account-holder?limit=10",
          scopeType: "holder",
        }),
        responseExample: `{
  "holders": [
    {
      "id": "${exampleIds.holder}",
      "name": "Atlas Family Office",
      "status": "active",
      "tax_id": "68.077.378/0001-27",
      "representative_tax_ids": ["123.456.789-09"],
      "group_ids": [],
      "tags": [],
      "created": "2026-05-02T14:11:08+00:00"
    }
  ],
  "cursor": ""
}`,
      },
      {
        id: "list-holder-events",
        method: "GET",
        path: "/account-holder/event",
        summary: "List Holder Events",
        description: [
          "Returns the scoped holder's event history — one snapshot per state change. See Events for the envelope shape.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated event ids.",
          },
          {
            name: "types",
            type: "string",
            description: "Filter by event type.",
          },
          {
            name: "after",
            type: "string",
            description: "Return events created after this date or timestamp.",
          },
          {
            name: "before",
            type: "string",
            description: "Return events created before this date or timestamp.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/account-holder/event?limit=10",
          scopeType: "holder",
        }),
      },
    ],
  },
  {
    id: "accounts",
    title: "Accounts",
    description: [
      "An Account is an operating unit inside a holder — a fund, an investment vehicle, or a desk. It is the boundary most of the API works within: investors, portfolios, and trades always belong to exactly one account, and you select it with the `Scope-Type: account` headers.",
      "Use accounts to segregate operations that must not mix — one account per fund keeps each fund's investors, positions, and reports isolated. The `unique_id` slug gives each account a stable, human-readable reference you can use in your own systems.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique account identifier." },
      { name: "name", type: "string", description: "Display name, 3 to 36 characters." },
      {
        name: "unique_id",
        type: "string",
        description:
          "Client-chosen slug, unique across the platform and immutable after creation.",
      },
      { name: "holder_id", type: "string", description: "Identifier of the owning holder." },
      {
        name: "status",
        type: "string",
        description: "One of `active`, `blocked`, `frozen`, or `closed`.",
      },
      { name: "tags", type: "list of strings", description: "Free-form labels attached to the account." },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "list-accounts",
        method: "GET",
        path: "/account",
        summary: "List Accounts",
        description: [
          "Returns the accounts visible in the current scope. With holder scope you get every account of the holder; with account scope you get that single account.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "status",
            type: "string",
            description: "Filter by status: `active`, `blocked`, `frozen`, or `closed`.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/account?status=active",
          scopeType: "holder",
        }),
        responseExample: `{
  "accounts": [
    {
      "id": "${exampleIds.account}",
      "name": "Atlas Global Fund",
      "unique_id": "atlas-global-fund",
      "holder_id": "${exampleIds.holder}",
      "status": "active",
      "tags": ["fund"],
      "created": "2026-05-02T14:20:41+00:00"
    }
  ],
  "cursor": ""
}`,
      },
      {
        id: "list-account-events",
        method: "GET",
        path: "/account/event",
        summary: "List Account Events",
        description: [
          "Returns the event history of the accounts in the current scope — one snapshot per state change. See Events for the envelope shape.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated event ids.",
          },
          {
            name: "resource.ids",
            type: "list of strings",
            description: "Comma-separated account ids, limiting events to those accounts.",
          },
          {
            name: "types",
            type: "string",
            description: "Filter by event type.",
          },
          {
            name: "after",
            type: "string",
            description: "Return events created after this date or timestamp.",
          },
          {
            name: "before",
            type: "string",
            description: "Return events created before this date or timestamp.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: `/account/event?resource.ids=${exampleIds.account}`,
          scopeType: "holder",
        }),
      },
    ],
  },
  {
    id: "instruments",
    title: "Instruments",
    description: [
      "Instruments are the catalog of everything that can be held, priced, and traded: bonds, stocks, currencies. Every trade and position references an instrument by id, and market data, pricing, and valuation all key off this catalog — so identifying the right instrument is the first step of any position-tracking integration.",
      "Instruments are split into classes, each with its own listing route: debt (fixed income), equity, and currency. All three routes return the same response key, `instruments`. If an instrument you need is missing from the catalog, request it through Instrument Requests.",
      "Every instrument carries a `scale`: the number of decimal places its quantities use. Quantities and prices across the API are integers paired with a scale, never floating-point numbers.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique instrument identifier." },
      { name: "symbol", type: "string", description: "Market symbol of the instrument." },
      { name: "name", type: "string", description: "Human-readable instrument name." },
      { name: "currency", type: "string", description: "Currency the instrument is denominated in." },
      { name: "country", type: "string", description: "Country of the instrument." },
      {
        name: "status",
        type: "string",
        description: "Whether the instrument is available for use.",
      },
      {
        name: "availability",
        type: "string",
        description:
          "`internal` for instruments tradable on the platform, `external` for track-only instruments.",
      },
      {
        name: "scale",
        type: "integer",
        description: "Decimal places used by quantities of this instrument.",
      },
      { name: "figi", type: "string", description: "OpenFIGI identifier." },
      { name: "isin", type: "string", description: "ISIN identifier, when known." },
      {
        name: "subtype",
        type: "string",
        description:
          "Class-specific subtype, such as `government-bond`, `corporate-bond`, `public-equity`, `currency-unit`, or `currency-pair`.",
      },
    ],
    endpoints: [
      {
        id: "list-debt-instruments",
        method: "GET",
        path: "/debt-instrument",
        summary: "List Debt / Fixed Income Instruments",
        description: [
          "Requires a scope — send `holder` or `account`; the catalog reads the same either way.",
          "Debt instruments additionally carry `due`, `coupon_frequency`, `issuer`, `debt_category`, `index`, `index_amount`, `index_scale`, `spread_amount`, `spread_scale`, and a read-only `display_rate` string.",
          "Pass `include_fields=price_amount` to enrich each item with its current best price.",
        ],
        queryParams: [
          ...instrumentListParams,
          {
            name: "debt_category",
            type: "string",
            description:
              "Filter by debt category, such as `ltn`, `ntn-b`, `lft`, or `standard-corporate`.",
          },
          {
            name: "subtype",
            type: "string",
            description: "Filter by subtype: `government-bond` or `corporate-bond`.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/debt-instrument?subtype=government-bond&limit=10",
          scopeType: "account",
        }),
        responseExample: `{
  "instruments": [
    {
      "id": "${exampleIds.debtInstrument}",
      "symbol": "NTN-B 2035",
      "name": "Tesouro IPCA+ 2035",
      "currency": "BRL",
      "country": "br",
      "status": "active",
      "availability": "external",
      "scale": 2,
      "due": "2035-05-15",
      "coupon_frequency": "semi-annual",
      "issuer": "Tesouro Nacional",
      "debt_category": "ntn-b",
      "index": "ipca",
      "display_rate": "IPCA + 6.21%",
      "subtype": "government-bond",
      "figi": "BBG00XYZ1234",
      "isin": "BRSTNCNTB6A5"
    }
  ],
  "cursor": ""
}`,
      },
      {
        id: "list-equity-instruments",
        method: "GET",
        path: "/equity-instrument",
        summary: "List Equity Instruments",
        description: [
          "Requires a scope — send `holder` or `account`; the catalog reads the same either way.",
          "Equity instruments additionally carry the `exchange` they trade on.",
        ],
        queryParams: instrumentListParams,
        requestExample: curl({
          method: "GET",
          path: `/equity-instrument?ids=${exampleIds.equityInstrument}`,
          scopeType: "account",
        }),
        responseExample: `{
  "instruments": [
    {
      "id": "${exampleIds.equityInstrument}",
      "symbol": "PETR4",
      "name": "Petrobras PN",
      "currency": "BRL",
      "country": "br",
      "exchange": "b3",
      "status": "active",
      "availability": "external",
      "scale": 0,
      "figi": "BBG000B9XRY4",
      "isin": "BRPETRACNPR6",
      "subtype": "public-equity"
    }
  ],
  "cursor": ""
}`,
      },
      {
        id: "list-currency-instruments",
        method: "GET",
        path: "/currency-instrument",
        summary: "List Currency Instruments",
        description: [
          "Requires a scope — send `holder` or `account`; the catalog reads the same either way.",
          "Currency instruments come in two subtypes. A `currency-unit` is a single fiat currency, such as `USD` or `BRL`: units denominate balances and settlements, and carry no market price of their own. A `currency-pair` is an FX pair, the priceable member of the class — pairs are what exchange rates are captured for.",
          "A pair's `symbol` concatenates the base currency and then the quote currency: `USDBRL` is US dollars priced in Brazilian reais. The base is what is bought; the quote is what pays for it, and the pair's `currency` field always carries the quote leg (`BRL` for `USDBRL`). Each direction is its own instrument — `USDBRL` and `BRLUSD` are separate records, quoted independently.",
          "The `scale` reads differently per subtype: for units it is the quantity scale of the currency, for pairs it is the display scale of the rate. Use `include_fields=price_amount` to include the current rate on pairs.",
        ],
        queryParams: [
          ...instrumentListParams,
          {
            name: "subtype",
            type: "string",
            description: "Filter by subtype: `currency-unit` or `currency-pair`.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/currency-instrument?limit=10",
          scopeType: "account",
        }),
        responseExample: `{
  "instruments": [
    {
      "id": "${exampleIds.currencyInstrument}",
      "symbol": "USD",
      "name": "US Dollar",
      "currency": "USD",
      "country": "us",
      "status": "active",
      "availability": "internal",
      "scale": 2,
      "figi": "",
      "subtype": "currency-unit"
    },
    {
      "id": "09c08b1e4f6a2c9d4b7e9f1a3c5d7b9e0f2a",
      "symbol": "USDBRL",
      "name": "US Dollar / Brazilian Real",
      "currency": "BRL",
      "country": "br",
      "status": "active",
      "availability": "internal",
      "scale": 4,
      "figi": "",
      "subtype": "currency-pair"
    }
  ],
  "cursor": ""
}`,
      },
    ],
  },
  {
    id: "instrument-request-preview",
    title: "Instrument Request Preview",
    description: [
      "A Preview is the search step before an Instrument Request. You give it the symbol your users know — a ticker, a bond name — and it returns the candidate instruments that symbol may resolve to, each identified by FIGI.",
      "Use it to disambiguate before committing: the same ticker can exist on several exchanges, and the preview lets you (or your user) pick the right candidate and carry its FIGI into the actual Instrument Request. Nothing is persisted by a preview.",
    ],
    endpoints: [
      {
        id: "create-instrument-request-preview",
        method: "POST",
        path: "/instrument-request/preview",
        summary: "Create Instrument Request Preview",
        description: [
          "Requires a holder in scope — send `holder` scope, or `account` scope and the account's holder is used. The body takes a `symbol` and optionally one of `exchange` or `country` to narrow the search — sending both is rejected.",
          "Each preview carries `figi`, `symbol`, `name`, `instrument_type`, `instrument_subtype`, and `exchange`. The `isin`, `currency`, and `country` fields are empty at the preview stage; full identity is established when the instrument request resolves.",
        ],
        bodyParams: [
          {
            name: "symbol",
            type: "string",
            required: true,
            description: "Market symbol to search for, 1 to 64 characters.",
          },
          {
            name: "exchange",
            type: "string",
            description: "Exchange to narrow the search. Cannot be combined with `country`.",
          },
          {
            name: "country",
            type: "string",
            description: "Country to narrow the search. Cannot be combined with `exchange`.",
          },
        ],
        requestExample: curl({
          method: "POST",
          path: "/instrument-request/preview",
          scopeType: "holder",
          body: `{"symbol": "PETR4", "country": "br"}`,
        }),
        responseExample: `{
  "previews": [
    {
      "figi": "BBG000B9XRY4",
      "isin": "",
      "symbol": "PETR4",
      "name": "Petrobras PN",
      "instrument_type": "equity",
      "instrument_subtype": "public-equity",
      "currency": "",
      "country": "",
      "exchange": "b3"
    }
  ]
}`,
      },
    ],
  },
  {
    id: "instrument-requests",
    title: "Instrument Requests",
    description: [
      "An Instrument Request asks Veltiz to add an instrument to the catalog, identified by FIGI or ISIN — typically the FIGI you picked from a preview. Once the request succeeds, the instrument is listable on its class route and can be referenced by trades and positions.",
      "Requests resolve asynchronously: they start as `created` and move to `success` or `failed`. On success, the resolved instrument id and type are recorded on the request's success event. Poll the list endpoint, filtering by `status`, to follow resolution.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique request identifier." },
      { name: "holder_id", type: "string", description: "Holder that made the request." },
      {
        name: "status",
        type: "string",
        description: "One of `created`, `success`, or `failed`.",
      },
      { name: "figi", type: "string", description: "Requested FIGI, empty when not given." },
      { name: "isin", type: "string", description: "Requested ISIN, empty when not given." },
      { name: "tags", type: "list of strings", description: "Free-form labels attached to the request." },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "create-instrument-requests",
        method: "POST",
        path: "/instrument-request",
        summary: "Create Instrument Requests",
        description: [
          "Requires a holder in scope — send `holder` scope, or `account` scope and the account's holder is used. Each item must carry at least one of `figi` or `isin`. Each holder has a cap on successful requests; in sandbox the cap is 10.",
        ],
        bodyParams: [
          {
            name: "requests",
            type: "array",
            required: true,
            description: "1 to 100 request objects.",
          },
          {
            name: "requests[].figi",
            type: "string",
            description: "FIGI of the instrument, 1 to 64 characters. Required when `isin` is absent.",
          },
          {
            name: "requests[].isin",
            type: "string",
            description: "ISIN of the instrument, 1 to 64 characters. Required when `figi` is absent.",
          },
        ],
        requestExample: curl({
          method: "POST",
          path: "/instrument-request",
          scopeType: "holder",
          body: `{"requests": [{"figi": "BBG000B9XRY4"}]}`,
        }),
        responseExample: `{
  "requests": [
    {
      "id": "${exampleIds.instrumentRequest}",
      "holder_id": "${exampleIds.holder}",
      "status": "created",
      "figi": "BBG000B9XRY4",
      "isin": "",
      "tags": [],
      "created": "2026-08-11T12:03:55+00:00"
    }
  ],
  "message": "Instrument Request successfully created."
}`,
      },
      {
        id: "list-instrument-requests",
        method: "GET",
        path: "/instrument-request",
        summary: "List Instrument Requests",
        description: [
          "Returns the scoped holder's requests, newest first. Requires a holder in scope — `holder` scope, or `account` scope and the account's holder is used.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated request ids, up to 100.",
          },
          {
            name: "status",
            type: "string",
            description: "Filter by status: `created`, `success`, or `failed`.",
          },
          {
            name: "after",
            type: "string",
            description: "Return requests created after this date or timestamp.",
          },
          {
            name: "before",
            type: "string",
            description: "Return requests created before this date or timestamp.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/instrument-request?status=success",
          scopeType: "holder",
        }),
      },
      {
        id: "list-instrument-request-events",
        method: "GET",
        path: "/instrument-request/event",
        summary: "List Instrument Request Events",
        description: [
          "Returns the scoped holder's request events — one snapshot per state change. Requires a holder in scope — `holder` scope, or `account` scope and the account's holder is used. See Events for the envelope shape.",
        ],
        queryParams: [
          {
            name: "limit",
            type: "integer",
            description: "Number of items to return, 1 to 100. Defaults to 100.",
          },
          {
            name: "cursor",
            type: "string",
            description: "Opaque cursor returned by a previous page.",
          },
          {
            name: "types",
            type: "list of strings",
            description:
              "Comma-separated event types, up to 10: `created`, `success`, or `failed`.",
          },
          {
            name: "before",
            type: "string",
            description: "Return events created before this date or timestamp.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/instrument-request/event?types=success",
          scopeType: "holder",
        }),
      },
    ],
  },
  {
    id: "investors",
    title: "Investors",
    description: [
      "An Investor is a person or entity whose holdings you manage inside an account — a family member in a family office, a shareholder in a fund. The investor is the legal owner the platform tracks holdings for, identified by their tax id.",
      "Investors exist to segregate ownership: each one owns portfolios, and every position ultimately answers to an investor. Investor routes require account scope and the tracker product.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique investor identifier." },
      { name: "account_id", type: "string", description: "Account the investor belongs to." },
      { name: "name", type: "string", description: "Full name of the investor." },
      { name: "tax_id", type: "string", description: "CPF or CNPJ, validated and stored formatted." },
      {
        name: "status",
        type: "string",
        description: "One of `active`, `blocked`, `frozen`, or `closed`.",
      },
      { name: "tags", type: "list of strings", description: "Free-form labels attached to the investor." },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "create-investors",
        method: "POST",
        path: "/investor",
        summary: "Create Investors",
        description: [
          "Creates investors in the scoped account. Each investor may optionally carry one portfolio to be created with it, in the same transaction. The created portfolio is not returned; read it back with `GET /portfolio` filtered by `owner_id`.",
        ],
        bodyParams: [
          {
            name: "investors",
            type: "array",
            required: true,
            description: "1 to 100 investor objects.",
          },
          {
            name: "investors[].name",
            type: "string",
            required: true,
            description: "Full name of the investor.",
          },
          {
            name: "investors[].tax_id",
            type: "string",
            required: true,
            description: "CPF or CNPJ. The checksum is validated.",
          },
          {
            name: "investors[].portfolios",
            type: "array",
            description:
              "At most one portfolio object with a mandatory `name` and optional `tags`.",
          },
        ],
        requestExample: curl({
          method: "POST",
          path: "/investor",
          scopeType: "account",
          body: `{"investors": [{"name": "Ana Souza", "tax_id": "123.456.789-09", "portfolios": [{"name": "Ana Souza Main"}]}]}`,
        }),
        responseExample: `{
  "investors": [
    {
      "id": "${exampleIds.investor}",
      "account_id": "${exampleIds.account}",
      "name": "Ana Souza",
      "tax_id": "123.456.789-09",
      "status": "active",
      "tags": [],
      "created": "2026-08-11T12:10:02+00:00"
    }
  ],
  "message": "Investor successfully created."
}`,
      },
      {
        id: "list-investors",
        method: "GET",
        path: "/investor",
        summary: "List Investors",
        description: ["Returns the investors of the scoped account."],
        queryParams: [
          ...paginationParams,
          {
            name: "status",
            type: "string",
            description: "Filter by status: `active`, `blocked`, `frozen`, or `closed`.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/investor?status=active",
          scopeType: "account",
        }),
      },
      {
        id: "list-investor-events",
        method: "GET",
        path: "/investor/event",
        summary: "List Investor Events",
        description: [
          "Returns the event history of the scoped account's investors — one snapshot per state change. See Events for the envelope shape.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "after",
            type: "string",
            description: "Return events created after this date or timestamp.",
          },
          {
            name: "before",
            type: "string",
            description: "Return events created before this date or timestamp.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/investor/event?limit=10",
          scopeType: "account",
        }),
      },
    ],
  },
  {
    id: "portfolios",
    title: "Portfolios",
    description: [
      "A Portfolio groups the positions of an owner — usually an investor — inside an account. It is the unit trades are booked against and the unit valuation is computed for: consolidated views, weights, and performance all resolve per portfolio.",
      "An investor can hold more than one portfolio to keep strategies or custodians apart — an onshore and an offshore book, for example. Portfolio routes require account scope and the tracker product.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique portfolio identifier." },
      { name: "scope_type", type: "string", description: "Scope the portfolio lives in, usually `account`." },
      { name: "scope_id", type: "string", description: "Identifier of that scope." },
      { name: "owner_type", type: "string", description: "Type of the owner, usually `investor`." },
      { name: "owner_id", type: "string", description: "Identifier of the owner." },
      { name: "name", type: "string", description: "Display name of the portfolio." },
      {
        name: "status",
        type: "string",
        description: "One of `active`, `blocked`, `frozen`, or `closed`.",
      },
      { name: "tags", type: "list of strings", description: "Free-form labels attached to the portfolio." },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "create-portfolios",
        method: "POST",
        path: "/portfolio",
        summary: "Create Portfolios",
        description: ["Creates portfolios in the scoped account."],
        bodyParams: [
          {
            name: "portfolios",
            type: "array",
            required: true,
            description: "1 to 100 portfolio objects.",
          },
          {
            name: "portfolios[].name",
            type: "string",
            required: true,
            description: "Display name of the portfolio.",
          },
          {
            name: "portfolios[].owner_type",
            type: "string",
            required: true,
            description: "Type of the owner, usually `investor`.",
          },
          {
            name: "portfolios[].owner_id",
            type: "string",
            required: true,
            description: "Identifier of the owner.",
          },
          {
            name: "portfolios[].tags",
            type: "list of strings",
            description: "Free-form labels to attach.",
          },
        ],
        requestExample: curl({
          method: "POST",
          path: "/portfolio",
          scopeType: "account",
          body: `{"portfolios": [{"name": "Ana Souza Offshore", "owner_type": "investor", "owner_id": "${exampleIds.investor}"}]}`,
        }),
        responseExample: `{
  "portfolios": [
    {
      "id": "${exampleIds.portfolio}",
      "scope_type": "account",
      "scope_id": "${exampleIds.account}",
      "owner_type": "investor",
      "owner_id": "${exampleIds.investor}",
      "name": "Ana Souza Offshore",
      "status": "active",
      "tags": [],
      "created": "2026-08-11T12:15:37+00:00"
    }
  ],
  "message": "Portfolio successfully created."
}`,
      },
      {
        id: "list-portfolios",
        method: "GET",
        path: "/portfolio",
        summary: "List Portfolios",
        description: ["Returns the portfolios of the scoped account."],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated portfolio ids, up to 30.",
          },
          {
            name: "owner_id",
            type: "string",
            description: "Filter by owner, such as an investor id.",
          },
          {
            name: "status",
            type: "list of strings",
            description:
              "Comma-separated statuses, such as `status=active,frozen`. Omit to return all.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: `/portfolio?owner_id=${exampleIds.investor}`,
          scopeType: "account",
        }),
      },
      {
        id: "list-portfolio-events",
        method: "GET",
        path: "/portfolio/event",
        summary: "List Portfolio Events",
        description: [
          "Returns the event history of the scoped account's portfolios — one snapshot per state change. See Events for the envelope shape.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "after",
            type: "string",
            description: "Return events created after this date or timestamp.",
          },
          {
            name: "before",
            type: "string",
            description: "Return events created before this date or timestamp.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/portfolio/event?limit=10",
          scopeType: "account",
        }),
      },
    ],
  },
  {
    id: "positions",
    title: "Positions",
    description: [
      "A Position records that a portfolio currently holds a non-zero balance of an instrument. Positions are a system-managed read model: the platform creates and closes them as trades book, so they cannot be created or edited directly — post trades, and the positions follow.",
      "The quantity is not stored: it is derived from the ledger on every read, and each position comes enriched with the instrument's current price and the resulting market value. `custody_type` separates `internal` positions, held in platform-connected custody, from `external` ones, tracked from external trades. Position routes require account scope and the tracker product.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique position identifier." },
      {
        name: "status",
        type: "string",
        description: "`active` or `closed`. Listings return only active positions.",
      },
      { name: "portfolio_id", type: "string", description: "Portfolio holding the position." },
      { name: "account_id", type: "string", description: "Account the position belongs to." },
      { name: "instrument_id", type: "string", description: "Identifier of the held instrument." },
      {
        name: "instrument_type",
        type: "string",
        description:
          "One of `debt`, `equity`, `currency`, or `option`. `currency` positions come from internal trade legs, never from an external trade.",
      },
      {
        name: "custody_type",
        type: "string",
        description: "`internal` or `external`.",
      },
      { name: "symbol", type: "string", description: "Current market symbol of the instrument." },
      {
        name: "symbol_amount",
        type: "integer",
        description: "Quantity currently held, derived from the ledger.",
      },
      {
        name: "symbol_scale",
        type: "integer",
        description: "Decimal places of symbol_amount.",
      },
      { name: "exchange", type: "string", description: "Exchange of the instrument, when it has one." },
      {
        name: "current_price_amount",
        type: "integer",
        description: "Latest observed price per unit, at the currency's scale.",
      },
      {
        name: "current_price_scale",
        type: "integer",
        description: "Decimal places of current_price_amount.",
      },
      {
        name: "total_amount",
        type: "integer",
        description:
          "Market value of the position: quantity times current price, with an option's price further scaled by its contract multiplier.",
      },
      {
        name: "total_scale",
        type: "integer",
        description: "Decimal places of total_amount.",
      },
      { name: "currency", type: "string", description: "Currency of the price and market value." },
      {
        name: "reference_time",
        type: "string",
        description: "When the current price was observed.",
      },
      {
        name: "average_price_amount / cost_amount",
        type: "integers",
        description:
          "Cost basis of the position, with their scale fields. Currently returned as `null`.",
      },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "list-positions",
        method: "GET",
        path: "/position",
        summary: "List Positions",
        description: [
          "Returns the active positions of the scoped account, with quantities read from the ledger and values marked at the latest price.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated position ids, up to 100.",
          },
          {
            name: "portfolio_id",
            type: "string",
            description: "Filter by portfolio.",
          },
          {
            name: "custody_type",
            type: "string",
            description: "Filter by custody type: `internal` or `external`.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: `/position?portfolio_id=${exampleIds.portfolio}`,
          scopeType: "account",
        }),
        responseExample: `{
  "positions": [
    {
      "id": "6d03f4b8a2c94d6e8f1b3a5c7d9e0b2f4a6c",
      "status": "active",
      "portfolio_id": "${exampleIds.portfolio}",
      "account_id": "${exampleIds.account}",
      "instrument_id": "${exampleIds.equityInstrument}",
      "instrument_type": "equity",
      "custody_type": "external",
      "symbol": "PETR4",
      "symbol_amount": 100,
      "symbol_scale": 0,
      "exchange": "b3",
      "current_price_amount": 3902,
      "current_price_scale": 2,
      "total_amount": 390200,
      "total_scale": 2,
      "currency": "BRL",
      "reference_time": "2026-08-11T21:00:00+00:00",
      "average_price_amount": null,
      "average_price_scale": null,
      "cost_amount": null,
      "cost_scale": null,
      "created": "2026-08-11T12:22:31+00:00"
    }
  ],
  "cursor": ""
}`,
      },
      {
        id: "get-position-summary",
        method: "GET",
        path: "/position/summary",
        summary: "Get Position Summary",
        description: [
          "Consolidates one portfolio: the NAV per currency, the NAV converted to a single consolidation currency, the FX rates used, each position's weight, and the allocation per instrument type. Weights arrive as ready-formatted percent strings — display values, not amounts.",
          "The consolidation currency comes from the `Accept-Currency` header, such as `usd` or `brl`, defaulting to `usd`. When a needed FX rate is unavailable, the consolidated `nav_amount` is `null` rather than a partial figure.",
        ],
        queryParams: [
          {
            name: "portfolio_id",
            type: "string",
            required: true,
            description: "Portfolio to consolidate.",
          },
        ],
        responseParams: [
          {
            name: "portfolio_id",
            type: "string",
            description: "The portfolio the summary describes.",
          },
          {
            name: "position_count",
            type: "integer",
            description: "Number of active positions included in the summary.",
          },
          {
            name: "navs",
            type: "array",
            description:
              "One entry per currency the portfolio holds positions in: the sum of the position market values in that currency, as `currency`, `nav_amount`, and `nav_scale`.",
          },
          {
            name: "consolidated_nav.currency",
            type: "string",
            description:
              "The consolidation currency, taken from the `Accept-Currency` header.",
          },
          {
            name: "consolidated_nav.nav_amount",
            type: "integer",
            description:
              "The whole portfolio's value converted to the consolidation currency, with `consolidated_nav.nav_scale` giving its decimal places. Returned as `null` when a needed FX rate is unavailable.",
          },
          {
            name: "consolidated_nav.rates",
            type: "array",
            description:
              "The FX rates used in the conversion: the pair `symbol`, `rate_amount`, `rate_scale`, and the `reference_time` the rate was observed.",
          },
          {
            name: "positions",
            type: "array",
            description:
              "One entry per position: its `id` and its `weight` — the share of the consolidated NAV, as a percent string. When the consolidated NAV is unavailable, the weight is computed within the position's own currency instead.",
          },
          {
            name: "allocations",
            type: "array",
            description:
              "The portfolio split by `instrument_type`, each with its `weight` of the consolidated NAV as a percent string. Empty when the consolidated NAV is unavailable.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: `/position/summary?portfolio_id=${exampleIds.portfolio}`,
          scopeType: "account",
        }),
        responseExample: `{
  "summary": {
    "portfolio_id": "${exampleIds.portfolio}",
    "position_count": 2,
    "navs": [
      { "currency": "BRL", "nav_amount": 390200, "nav_scale": 2 },
      { "currency": "USD", "nav_amount": 150000, "nav_scale": 2 }
    ],
    "consolidated_nav": {
      "currency": "usd",
      "nav_amount": 222259,
      "nav_scale": 2,
      "rates": [
        {
          "symbol": "USDBRL",
          "rate_amount": 54000,
          "rate_scale": 4,
          "reference_time": "2026-08-11T21:00:00+00:00"
        }
      ]
    },
    "positions": [
      { "id": "6d03f4b8a2c94d6e8f1b3a5c7d9e0b2f4a6c", "weight": "32.51%" },
      { "id": "6d032c8a5e1f9b3d4c7a8e0f2a4b6c8d0e1f", "weight": "67.49%" }
    ],
    "allocations": [
      { "instrument_type": "equity", "weight": "32.51%" },
      { "instrument_type": "currency", "weight": "67.49%" }
    ]
  }
}`,
      },
    ],
  },
  {
    id: "external-trades",
    title: "External Trades",
    description: [
      "An External Trade records a buy or sell executed outside the platform — at a broker, a custodian, or another administrator — so off-platform positions can be tracked and valued alongside everything else. It is how existing holdings enter the system: post the trades and the platform derives the positions.",
      "The execution price is stored for cost basis; no cash movement is booked. Trades cannot be amended: a trade is created, processes asynchronously, and a successful trade can only be canceled — cancellation reverses its position effect. A trade carrying a `snapshot_id` is refused with `position-snapshot-managed` instead; cancel the owning Position Snapshot to reverse it. External trade routes require account scope and the tracker product.",
      "A trade a Trade Parse Request creates carries a deterministic `unique_id`, `parse-request/{request_id}/{row_number}`, and two `tags`: `attachment/{attachment_id}` naming the file it came from, and `parse-request/{request_id}` naming the request itself — the shape to filter on to reconcile which trades one parse produced.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique trade identifier." },
      {
        name: "status",
        type: "string",
        description: "One of `created`, `processing`, `success`, `canceled`, or `failed`.",
      },
      { name: "portfolio_id", type: "string", description: "Portfolio the trade belongs to." },
      { name: "account_id", type: "string", description: "Account the trade belongs to." },
      { name: "instrument_id", type: "string", description: "Identifier of the traded instrument." },
      {
        name: "instrument_type",
        type: "string",
        description:
          "One of `debt`, `equity`, or `option`: the instrument types a trade can book today. Derived from `instrument_id` when the trade is recorded, never sent by the caller. A fund instrument id is refused `not-found`, until that class becomes bookable. A currency instrument id is checked only against the ledger's own USD and BRL ids: one of those two is refused `instrument-type-not-bookable`; any other currency instrument id, including a real seeded currency pair, is refused `not-found` instead, since it is never checked against the catalog.",
      },
      {
        name: "symbol",
        type: "string",
        description:
          "Market symbol, resolved from the instrument when the trade is recorded and kept as it was then; never sent by the caller.",
      },
      { name: "side", type: "string", description: "buy or sell." },
      {
        name: "symbol_amount",
        type: "integer",
        description: "Traded quantity as an integer, at least 1.",
      },
      {
        name: "symbol_scale",
        type: "integer",
        description: "Decimal places of symbol_amount, 0 to 18.",
      },
      {
        name: "price_amount",
        type: "integer",
        description:
          "Execution price per unit as an integer. `null` on an adjustment trade issued by a Position Snapshot that declared no cost basis.",
      },
      {
        name: "price_scale",
        type: "integer",
        description: "Decimal places of price_amount, 0 to 18.",
      },
      {
        name: "currency",
        type: "string",
        description:
          "Currency the instrument is denominated in, resolved from the instrument; never sent by the caller.",
      },
      {
        name: "reference_time",
        type: "string",
        description:
          "Execution instant, in ISO 8601. A bare `YYYY-MM-DD` sent by the caller is read as midnight in Brasília time and returned as that full instant, never the bare date sent.",
      },
      {
        name: "unique_id",
        type: "string",
        description: "Client-supplied duplicate guard, empty when not given.",
      },
      {
        name: "snapshot_id",
        type: "string",
        description:
          "Identifier of the Position Snapshot that produced this trade, empty when the trade was not issued by a snapshot adjustment.",
      },
      { name: "tags", type: "list of strings", description: "Free-form labels attached to the trade." },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "create-external-trades",
        method: "POST",
        path: "/external-trade",
        summary: "Create External Trades",
        description: [
          "Books external trades against portfolios of the scoped account. Quantities and prices are integers paired with a scale: a quantity of 12.5 is `symbol_amount` 125 with `symbol_scale` 1.",
          "The instrument travels as `instrument_id` alone: sending `instrument_type`, `symbol`, `currency`, `source`, or `snapshot_id` in the body is refused with `invalid-structure` — the platform resolves the first three from the instrument when the trade is recorded, no longer accepts `source` at all, and sets `snapshot_id` only through snapshot reconciliation, never from the caller.",
          "The optional `unique_id` protects against duplicates: a `unique_id` already recorded for the account, or repeated inside one batch, rejects the whole request and nothing is written.",
        ],
        bodyParams: [
          {
            name: "trades",
            type: "array",
            required: true,
            description: "1 to 100 trade objects.",
          },
          {
            name: "trades[].portfolio_id",
            type: "string",
            required: true,
            description: "Portfolio to book the trade against.",
          },
          {
            name: "trades[].instrument_id",
            type: "string",
            required: true,
            description: "Identifier of the traded instrument. Its type, symbol, and currency are derived from this id.",
          },
          {
            name: "trades[].side",
            type: "string",
            required: true,
            description: "`buy` or `sell`.",
          },
          {
            name: "trades[].symbol_amount",
            type: "integer",
            required: true,
            description: "Traded quantity as an integer, at least 1.",
          },
          {
            name: "trades[].symbol_scale",
            type: "integer",
            required: true,
            description: "Decimal places of symbol_amount, 0 to 18.",
          },
          {
            name: "trades[].price_amount",
            type: "integer",
            required: true,
            description: "Execution price per unit as an integer, at least 0.",
          },
          {
            name: "trades[].price_scale",
            type: "integer",
            required: true,
            description: "Decimal places of price_amount, 0 to 18.",
          },
          {
            name: "trades[].reference_time",
            type: "string",
            required: true,
            description: "A date `YYYY-MM-DD` or a timestamp with a UTC offset. A bare date means midnight in Brasília time.",
          },
          {
            name: "trades[].unique_id",
            type: "string",
            description: "Duplicate guard, unique per account, 1 to 64 characters.",
          },
          {
            name: "trades[].tags",
            type: "list of strings",
            description: "Free-form labels to attach.",
          },
        ],
        requestExample: curl({
          method: "POST",
          path: "/external-trade",
          scopeType: "account",
          body: `{"trades": [{"portfolio_id": "${exampleIds.portfolio}", "instrument_id": "${exampleIds.equityInstrument}", "side": "buy", "symbol_amount": 100, "symbol_scale": 0, "price_amount": 3841, "price_scale": 2, "reference_time": "2026-08-10", "unique_id": "broker-42-20260810-001"}]}`,
        }),
        responseExample: `{
  "trades": [
    {
      "id": "${exampleIds.externalTrade}",
      "status": "created",
      "portfolio_id": "${exampleIds.portfolio}",
      "account_id": "${exampleIds.account}",
      "instrument_id": "${exampleIds.equityInstrument}",
      "instrument_type": "equity",
      "symbol": "PETR4",
      "side": "buy",
      "symbol_amount": 100,
      "symbol_scale": 0,
      "price_amount": 3841,
      "price_scale": 2,
      "currency": "BRL",
      "reference_time": "2026-08-10T03:00:00+00:00",
      "unique_id": "broker-42-20260810-001",
      "snapshot_id": "",
      "tags": [],
      "created": "2026-08-11T12:22:19+00:00"
    }
  ],
  "message": "External Trade successfully created."
}`,
      },
      {
        id: "list-external-trades",
        method: "GET",
        path: "/external-trade",
        summary: "List External Trades",
        description: ["Returns the external trades of the scoped account."],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated trade ids, up to 100.",
          },
          {
            name: "portfolio_id",
            type: "string",
            description: "Filter by portfolio.",
          },
          {
            name: "status",
            type: "string",
            description:
              "Filter by status: `created`, `processing`, `success`, `canceled`, or `failed`.",
          },
          {
            name: "unique_id",
            type: "string",
            description: "Find the trade carrying this duplicate guard.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: `/external-trade?portfolio_id=${exampleIds.portfolio}&status=success`,
          scopeType: "account",
        }),
      },
      {
        id: "list-external-trade-events",
        method: "GET",
        path: "/external-trade/event",
        summary: "List External Trade Events",
        description: [
          "Returns the event history of the scoped account's external trades — one snapshot per state change. See Events for the envelope shape.",
          "Trade event types are finer than trade statuses: `booked` marks the position hitting the ledger and shows as status `processing`, and `voided` marks the effects being reversed and shows as status `canceled`. A `failed` event carries the causing errors in `reasons`.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated event ids.",
          },
          {
            name: "trade_ids",
            type: "list of strings",
            description: "Comma-separated trade ids, limiting events to those trades.",
          },
          {
            name: "types",
            type: "string",
            description:
              "Filter by event type: `created`, `booked`, `success`, `canceled`, `voided`, or `failed`.",
          },
          {
            name: "after",
            type: "string",
            description: "Return events created after this date or timestamp.",
          },
          {
            name: "before",
            type: "string",
            description: "Return events created before this date or timestamp.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: `/external-trade/event?trade_ids=${exampleIds.externalTrade}`,
          scopeType: "account",
        }),
      },
    ],
  },
  {
    id: "attachments",
    title: "Attachments",
    description: [
      "An Attachment is one stored file. It is how a document enters the platform, and the only place file content is kept — everything that later reads those bytes reads them from here.",
      "Nothing about the file is taken from the request's word: the content type is detected by reading the bytes, the size and digest are measured server-side, and the file name is sanitized and kept for display only — it is never used as a path and never interpreted.",
      "Every upload is scanned for malware before its bytes can be read back. A new attachment is `created` until the scan lands, then `success`, `rejected`, or `failed`. Only a `success` attachment can be downloaded or parsed.",
      "An attachment is immutable: there is no update route. To replace a file, upload the corrected one — it is a different attachment.",
      "Attachment routes take the scope from the `Scope-Type` and `Scope-Id` headers and accept either `holder` or `account` scope. Unlike the portfolio-tracking routes, they require no product.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique attachment identifier." },
      {
        name: "status",
        type: "string",
        description: "One of `created`, `success`, `rejected`, or `failed`.",
      },
      {
        name: "scope_id",
        type: "string",
        description: "Identifier of the holder or account the attachment belongs to.",
      },
      { name: "scope_type", type: "string", description: "`holder` or `account`." },
      { name: "file_name", type: "string", description: "Sanitized original file name. Display only." },
      {
        name: "content_type",
        type: "string",
        description: "MIME type detected from the stored bytes, never the one the request declared.",
      },
      {
        name: "byte_size",
        type: "integer",
        description: "Exact stored size in bytes, measured server-side.",
      },
      {
        name: "unique_id",
        type: "string",
        description: "Client-supplied upload key, empty when not given.",
      },
      { name: "tags", type: "list of strings", description: "Free-form labels attached to the attachment." },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "create-attachment",
        method: "POST",
        path: "/attachment",
        summary: "Upload an Attachment",
        description: [
          "The one endpoint on the platform that is `multipart/form-data` rather than JSON, because a file cannot ride in a JSON body. It carries exactly two parts: `file`, the raw bytes, and `attachments`, a JSON body of the usual array shape holding exactly one object.",
          "The request is signed over the exact multipart bytes — see File Imports before writing the call.",
          "The file is capped at 30 MiB. Parse requests read CSV only; binary formats are not yet supported through the public API.",
          "Two uniqueness guards run before any byte is stored, and either one refuses the whole request: a `unique_id` already used in the scope is refused with `already-exists`, and content the scope already holds is refused with `duplicate-file`, whose message names the attachment that already holds it.",
          "A `unique_id` stays claimed for 30 days whatever becomes of the upload, so re-sending a `failed` attachment needs a new one. The content guard is different: identical bytes may be uploaded again once their earlier attachment is `failed`.",
          "Other refusals: `invalid-structure` when the parts are not the two expected ones, `oversized-content` when the file is over 30 MiB, and `content-type-not-allowed` when the bytes match no accepted format.",
        ],
        bodyParams: [
          {
            name: "file",
            type: "multipart part",
            required: true,
            description: "The raw file bytes. Its `filename` becomes `file_name` after sanitization. Up to 30 MiB.",
          },
          {
            name: "attachments",
            type: "multipart part",
            required: true,
            description: "A JSON object of the standard array shape, holding exactly one attachment object.",
          },
          {
            name: "attachments[].unique_id",
            type: "string",
            description:
              "Upload key, unique per scope, 1 to 64 characters. Use it to find out whether an upload that timed out actually landed.",
          },
          {
            name: "attachments[].tags",
            type: "list of strings",
            description: "Free-form labels, up to 20 tags of up to 64 characters each.",
          },
        ],
        requestExample: curl({
          method: "POST",
          path: "/attachment",
          scopeType: "account",
          contentType: "multipart/form-data; boundary=$BOUNDARY",
          dataBinary: "upload-body.bin",
        }),
        responseExample: `{
  "attachments": [
    {
      "id": "${exampleIds.attachment}",
      "status": "created",
      "scope_id": "${exampleIds.account}",
      "scope_type": "account",
      "file_name": "statement.csv",
      "content_type": "text/csv",
      "byte_size": 8241,
      "unique_id": "xp-statement-2026-07",
      "tags": ["statement-2026-07"],
      "created": "2026-08-14T10:02:44+00:00"
    }
  ],
  "message": "Attachment successfully created."
}`,
      },
      {
        id: "list-attachments",
        method: "GET",
        path: "/attachment",
        summary: "List Attachments",
        description: [
          "Returns the scope's attachments, newest first.",
          "`ids` and `status` combine into a query whose size is capped: a request whose number of ids multiplied by its number of statuses is over 30 is refused with `filter-combination-too-large`. Thirty ids with one status is accepted; thirty ids with four statuses is not.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated attachment ids, up to 30.",
          },
          {
            name: "status",
            type: "list of strings",
            description: "Comma-separated statuses: `created`, `success`, `rejected`, or `failed`.",
          },
          {
            name: "unique_id",
            type: "string",
            description: "Find the attachment carrying this upload key.",
          },
          {
            name: "tags",
            type: "list of strings",
            description: "Comma-separated tags, up to 20. An attachment must carry every tag requested.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/attachment?status=success&limit=10",
          scopeType: "account",
        }),
      },
      {
        id: "get-attachment",
        method: "GET",
        path: "/attachment/{attachment_id}",
        summary: "Get an Attachment",
        description: [
          "Returns one attachment's metadata by id. An id belonging to another scope answers `not-found`, the same as an id that does not exist. Metadata is readable from `created` onward — only the bytes wait for the scan.",
        ],
        requestExample: curl({
          method: "GET",
          path: `/attachment/${exampleIds.attachment}`,
          scopeType: "account",
        }),
        responseExample: `{
  "attachment": {
    "id": "${exampleIds.attachment}",
    "status": "success",
    "scope_id": "${exampleIds.account}",
    "scope_type": "account",
    "file_name": "statement.csv",
    "content_type": "text/csv",
    "byte_size": 8241,
    "unique_id": "xp-statement-2026-07",
    "tags": ["statement-2026-07"],
    "created": "2026-08-14T10:02:44+00:00"
  }
}`,
      },
      {
        id: "download-attachment",
        method: "GET",
        path: "/attachment/{attachment_id}/stream",
        summary: "Download an Attachment",
        description: [
          "Streams the stored bytes back, with the detected `content_type` and a `Content-Disposition` carrying the sanitized file name. This is the only route that returns something other than JSON.",
          "Bytes are served only at status `success` — and even then, only when the stored object is still retrievable; a `success` attachment whose bytes have gone missing answers `not-stored` too. Every other status refuses with a code that says what to do next: `created` answers `not-scanned` (the scan has not landed yet, retry shortly), `rejected` answers `quarantined` (the file was found malicious and is quarantined permanently, never retry), and `failed` answers `not-stored` (the bytes never became retrievable, upload the file again).",
        ],
        requestExample: curl({
          method: "GET",
          path: `/attachment/${exampleIds.attachment}/stream`,
          scopeType: "account",
        }),
      },
      {
        id: "list-attachment-events",
        method: "GET",
        path: "/attachment/event",
        summary: "List Attachment Events",
        description: [
          "Returns the scope's attachment events — one snapshot per state change. See Events for the envelope shape.",
          "A `failed` event names its cause in `reasons`: `stored-object-missing`, `digest-mismatch`, or `scan-refused`.",
        ],
        queryParams: [...paginationParams],
        requestExample: curl({
          method: "GET",
          path: "/attachment/event?limit=10",
          scopeType: "account",
        }),
      },
    ],
  },
  {
    id: "parse-requests",
    title: "Parse Requests",
    description: [
      "A parse request turns one uploaded file into rows of one kind of operation. It names the attachment to read and the portfolio the rows belong to; which operation the rows become is fixed by the resource itself, never by a field. A Position Parse Request, at `/position-parse-request`, reads a statement of holdings into position snapshots. A Trade Parse Request, at `/trade-parse-request`, reads a blotter of executions into external trades. The two are one family — the same pipeline, the same permissions, the same result shape, and the same attributes, except `reference_time`, which only a holdings file needs — and each has its own five routes below. One request writes to exactly one portfolio.",
      "The two routes do not fail alike when the wrong file is posted. A snapshot's only mandatory column is quantity, and every trade blotter already carries a quantity column, so a blotter posted to `/position-parse-request` derives a schema and parses successfully — silently booking position snapshots from trade executions against the portfolio. The reverse fails loudly instead: a holdings file posted to `/trade-parse-request` cannot supply the price and date a trade needs, so it fails with `no-rows-mapped` and the file-shape reason described below. Pick the route by what the file actually is — a statement of holdings, or a blotter of executions — never by which route happens to accept it.",
      "The attachment must have reached status `success` and carry a supported content type, both checked when the request is created: an unscanned or quarantined attachment is refused with `invalid-attachment`, and a format that cannot be parsed with `content-type-unsupported`. CSV is the only format read today.",
      "Parsing is asynchronous. A request is `created`, moves through `processing`, and settles at one of four terminal statuses. `created` and `processing` are still running — the result route refuses a request that has not finished. `success` means every row created its resource, and `partial` means some rows created resources and the rest were refused. `rejected` means no row created a resource and at least one was refused. `failed` means nothing was ever attempted: the latest event's `reasons` says why — `no-rows-mapped` when no column or row of the file could be read, carrying `file-shape-position-snapshot` or `file-shape-external-trade` beside it when the file's columns map for the other route's shape instead, or a reason naming the credential when the access that asked for the parse was withdrawn before it ran. Rows that could not be read are refused one by one and reported with their reasons, so a file with a few bad rows still imports the rest.",
      "Rows are created with exactly the authority of the credential that asked for the parse. A credential that may not write to the portfolio produces no rows.",
      "Every parse-request route, in either family, requires `account` scope and the tracker product.",
      "Parsing reads CSV with a comma, semicolon, or tab as the delimiter — whichever the header line uses most. A column header matches when its normalised form is a known alias in the table of names, in English and Portuguese: normalising lowercases the header, strips accents, and folds brackets, spaces and hyphens into underscores, so a qualifier becomes part of the name rather than being dropped — `Price (Local)` normalises to `price_local`, which matches because `price_local` is a known alias.",
      "The decimal separator and the date order are decided once per file, from every cell of every numeric and date column, then applied to every row — so one file never books two readings of the same characters. A slashed date is read day-first unless the file proves otherwise; an ISO date is read as itself. A leading `-` and an accounting `(1.234)` both mean negative.",
      "Mandatory columns are a quantity, a price, and a date for a trade, or a quantity alone for a snapshot. A snapshot row carries no date of its own: the request's `reference_time` dates every row and the completeness declaration alike, and a date column present in the file is ignored. A trade row has no such fallback: a blotter dates its own rows, which is why a Trade Parse Request carries no `reference_time`. Every row must also carry something that identifies the instrument, in one of the shapes the resolver accepts: a FIGI, an ISIN, a tax id, an option descriptor such as `KORU Dec18'26 10 Put`, a symbol with its exchange, or a symbol alone. A Brazilian government bond is a symbol alone, written in any form the catalog reads: the catalog symbol `NTN-B|2035-05-15`; the category code with a full date, ISO or day-first — `NTN-B 2035-05-15`, `NTNB 15/05/2035` — or with a year only, `NTN-B 2035`; or the Tesouro Direto name with a year — `Tesouro Prefixado 2032`, `Tesouro Prefixado com Juros Semestrais 2033`, `Tesouro Selic 2029`, `Tesouro IPCA+ com Juros Semestrais 2055`, `Tesouro IPCA+ 2035`. A Tesouro Direto name resolves to the maturity Tesouro Direto sells for that year. A code with only a year must match exactly one maturity in that year; when it matches several, the row is refused with `instrument-maturity-ambiguous` and asks for the full date. A month-first date, a two-digit year, or a year and month with no day is not read as a bond. An `exchange` beside a bond is ignored. Any other symbol with no exchange beside it is not a rejection either: the catalog looks it up among equity listings across the exchanges it knows — never against a corporate-bond or option instrument, whatever exchange it trades on. A symbol it finds on exactly one listing resolves; one that matches more than one listing is refused with `instrument-symbol-ambiguous`, and the fix is adding an `exchange` column, not an Instrument Request.",
      "Side comes from a side column when the file has one, read through the known names for buy and sell, and an unreadable side value refuses the row. A trade with no side column reads the sign of its quantity as the side: positive buys, negative sells, and zero refuses the row, naming the quantity, because a quantity of zero states no direction. For a trade, a mapped side column left blank is different from having no side column at all: its empty cell is refused with `invalid-structure`, and a filled cell the alias table does not recognize with `invalid-value`. A snapshot has no side of its own: its quantity is signed, so a side column signs the declared quantity, `sell` negative and `buy` positive, and without one — no column, or a column with a blank cell — the quantity is kept exactly as written, zero included.",
      "Instruments are resolved as the file is read, against every identity shape above. One the catalog already holds is found immediately. One it does not hold — other than a tax id, which only ever matches an existing fund — is looked up with the market-data provider and created on the spot when the provider names exactly one candidate for it. A tax id that resolves still cannot book today: a fund instrument id is refused `not-found` at booking (see `instrument_type` below), so a tax-id row can succeed at parse time and still fail one step later. A row the catalog cannot resolve to one instrument is refused with the reason that names its cause, and each cause has its own fix: `unresolved-instrument` — the row named no instrument at all; add a symbol, ISIN, FIGI or CNPJ column. `instrument-unresolvable` — the identifier names nothing the catalog or its providers know; register the instrument through an Instrument Request and parse again. `instrument-symbol-ambiguous` — the symbol trades on several venues; add an `exchange` column. `instrument-maturity-ambiguous` — the bond code matches several maturities in that year, and the message lists them; write the full maturity date. `debt-category-unsupported` — the row names a government-bond category the platform does not track yet, such as NTN-C, Renda+ or Educa+. `instrument-catalog-conflict` — the catalog already holds this bond under a different availability; contact support. A provider budget or rate limit never refuses a row — resolution is retried instead.",
      "What is deliberately not booked: a row whose `security_type` cell names a derivative or an FX contract is refused with `security-type-unsupported`, and a row whose multiplier is anything but 1 with `multiplier-unsupported`. A listed option is the exception on both counts — its `multiplier` cell may read 100, not just 1 — but only when its `symbol` column holds a recognised option descriptor. The cell is checked, never booked: an option's contract size always comes from its catalog entry, 100 for every option the platform creates, not from the file. The `security_type` check runs first: it refuses whenever the cell holds one of eight recognised derivative words, and is waived only when that word names an option and the descriptor is present. A cell that is blank, or names anything else, never fires the check, leaving the multiplier check as the row's only guard. An option identified instead by FIGI or ISIN — whose `symbol` column carries no descriptor — gets no such waiver: it refuses `security-type-unsupported`, like any other row, whenever its `security_type` cell names a derivative. A descriptor row has two option-specific ways to fail: a leg descriptor with no strategy row above it naming the underlying is refused with `option-group-context-missing`, and a descriptor whose terms cannot be read is refused with `invalid-value`. A row that names no security type but still cannot be matched to an instrument — a Bloomberg-style FX ticker with no FIGI, for example — falls through to the resolver instead and is refused with `instrument-unresolvable`. Commission, gross notional, running profit, and a per-row currency are read and kept in the row's `raw` but never booked — a trade books its gross price, and the instrument carries the currency.",
      "A Position Parse Request also declares completeness. Set `is_complete` true and, once every row of the file has created its resource, the platform posts one Portfolio Snapshot for the portfolio at the request's `reference_time` — closing every external position the file does not list as of that instant. The declaration carries a deterministic `unique_id`, `parse-request/{request_id}/portfolio-snapshot`, and the same two `tags` as the file's rows — `attachment/{attachment_id}` and `parse-request/{request_id}` — the shape to filter on to find it. The declaration is all-or-nothing: a request that ends `partial` (a rejected row), `rejected` (every row rejected), or `failed` (nothing attempted) makes none, whatever `is_complete` asked for, and the request itself reads back `is_complete: false` with `completeness-not-applied` in `reasons`. Fix the rejected rows and re-upload with the flag set again. A Trade Parse Request never declares completeness; the field does not exist on that route.",
      "A finished request's rows are read from its family's result route, split by outcome: `success_rows` for the rows that created a resource, `rejected_rows` for the rows that were refused. Both arrays are always present; an outcome with no rows is an empty array. Every row carries its `row_number`, counting from 1 at the first record after the header, and `raw`, the row's own cells keyed by the column header exactly as the file wrote it — so a refused row can be found and fixed in the original file. A success row also carries the id of what it created: `snapshot_id` on a Position Parse Request, `trade_id` on a Trade Parse Request. The id is empty when the row had already been created by an earlier attempt at the same request.",
      "A rejected row carries `reasons`, the same error shape the API returns elsewhere. An empty cell in a mandatory column — the quantity, and on a trade also the price, the date, and a mapped side column — is refused with `invalid-structure`; a filled cell that cannot be read is refused with `invalid-value`, and so is a negative price or average price, a tax id that fails its check, or an identifier over the parser's 64-character cap. An empty multiplier, average price, tax id, or snapshot side cell is simply not read. Each reason names the field in its `parameter` (`unit_price` for a snapshot's price column when no average-price column is mapped); these are the most common rejections in practice. The parser's own codes are `unresolved-instrument`, `security-type-unsupported`, `multiplier-unsupported`, `option-group-context-missing`, `row-columns-mismatch` and `exchange-unknown`. The last one is different in kind from `invalid-value`: the cell was read perfectly well, and it is the venue it names that the platform has no exchange for, so the reason quotes the value back to you rather than telling you the cell is unreadable. A row can also carry any of the instrument catalog's codes listed above, any code the operation itself returned, or, when the access that asked for the parse was withdrawn while it ran, `creator-not-assertable` or `creator-access-revoked`.",
      "The result route answers only once the request has reached a terminal status. A request still `created` or `processing` is refused with `invalid-value` on `path['request_id']`, naming the status it is at — reporting empty arrays would make a request that has not run read like one that parsed nothing. The route takes no query parameters and is not paginated: it returns every row of both outcomes in one response.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique request identifier." },
      {
        name: "status",
        type: "string",
        description: "One of `created`, `processing`, `success`, `partial`, `rejected`, or `failed`.",
      },
      { name: "attachment_id", type: "string", description: "Attachment being parsed." },
      { name: "account_id", type: "string", description: "Account the request belongs to." },
      { name: "portfolio_id", type: "string", description: "Portfolio every row of this request is written to." },
      {
        name: "unique_id",
        type: "string",
        description:
          "Duplicate guard, unique per account within its own route — the same value may name one Position Parse Request and one Trade Parse Request. Filled by the platform when not supplied.",
      },
      {
        name: "reference_time",
        type: "string",
        description:
          "Position Parse Request only. The instant that dates every snapshot row and the completeness declaration alike, in ISO 8601. Before it reaches a row, the value is reduced to its UTC calendar date, discarding the time of day, and that date is re-read as midnight in Brasília time — for a 2026 date that is `T03:00:00+00:00` — so a caller-supplied instant books at Brasília midnight on its UTC calendar date, not at the instant sent, and can land a day later near the day boundary. A bare `YYYY-MM-DD` supplied here goes through the same re-read and also means midnight in Brasília time; left out entirely, it defaults to 00:00 UTC of the day the request is created. A Trade Parse Request has no such attribute: a blotter dates its own rows.",
      },
      {
        name: "is_complete",
        type: "boolean",
        description:
          "Position Parse Request only, mandatory on `POST`. `true` declares the file the complete statement of the portfolio's holdings at `reference_time`: once every row is created, the platform posts one Portfolio Snapshot that closes every position the file does not list as of that instant. Flipped to `false`, with `completeness-not-applied` in `reasons`, on a terminal event that made no declaration — a `partial` or `rejected` request with a refused row, or a `failed` request that attempted nothing. A Trade Parse Request has no such attribute: sending it is refused with `invalid-structure`, like any unknown key.",
      },
      {
        name: "results",
        type: "array",
        description:
          "Present only when `include_fields=results` is asked for: one entry per outcome, each with `id`, `outcome`, and `row_count`; the family is the route the request was read from. Never row content — the rows themselves come from the request's own result route, which is the only way to read them.",
      },
      {
        name: "reasons",
        type: "array",
        description:
          "Carried on every read of a request — `GET /{family}` and `GET /{family}/{request_id}` — as `[]` when there is nothing to report. Holds the latest event's reasons in the same `{code, message}` shape a rejected row's `reasons` uses: `no-rows-mapped` (with a `file-shape-*` code beside it) on a `failed` request that attempted nothing, `completeness-not-applied` on a Position Parse Request whose `is_complete` was true but the request ended `partial`, `rejected`, or `failed` — no declaration was made, and this names why — otherwise empty. Not present on the `POST` response.",
      },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "create-position-parse-requests",
        legacyId: "create-parse-requests",
        method: "POST",
        path: "/position-parse-request",
        summary: "Create Position Parse Requests",
        description: [
          "Creates holdings requests in the scoped account and starts each one. Every item names an attachment at status `success` and the portfolio its snapshots belong to; the route fixes what the rows become, so the body carries no `type` — sending one is refused with `invalid-structure`, like any unknown key.",
          "The optional `unique_id` is the duplicate guard: a value already recorded for the account's holdings requests is refused with `already-exists`, and one repeated inside a batch with `invalid-value`. When it is omitted the platform generates one, and it comes back in the response like any other field — a caller that lost its response reads the request back with `GET /position-parse-request?attachment_id=…` instead of guessing what was assigned.",
        ],
        bodyParams: [
          {
            name: "requests",
            type: "array",
            required: true,
            description: "1 to 100 request objects.",
          },
          {
            name: "requests[].attachment_id",
            type: "string",
            required: true,
            description: "Attachment to parse. Must be at status `success`.",
          },
          {
            name: "requests[].portfolio_id",
            type: "string",
            required: true,
            description: "Portfolio every snapshot of this request is written to.",
          },
          {
            name: "requests[].is_complete",
            type: "boolean",
            required: true,
            description:
              "Declares whether the file is the complete statement of the portfolio's holdings at `reference_time`. `true` posts one Portfolio Snapshot for the portfolio once every row is created, closing every position the file does not list as of that instant; `false` books the rows with no such declaration. Missing is refused with `invalid-structure`; a non-boolean value with `invalid-type`.",
          },
          {
            name: "requests[].unique_id",
            type: "string",
            description: "Duplicate guard, unique per account among holdings requests, 1 to 128 characters.",
          },
          {
            name: "requests[].reference_time",
            type: "string",
            description:
              "The instant that dates every snapshot row and the completeness declaration alike: a calendar date or an instant carrying a UTC offset — but any instant is first reduced to its UTC calendar date, discarding the time of day, and that date is re-read as midnight in Brasília time — for a 2026 date that is `T03:00:00+00:00` — so it books at Brasília midnight, not at the instant sent, and can land a day later near the day boundary. A bare date goes through the same re-read. Left out entirely, it defaults to the start of the current UTC day. A date column present in the file itself is ignored.",
          },
        ],
        requestExample: curl({
          method: "POST",
          path: "/position-parse-request",
          scopeType: "account",
          body: `{"requests": [{"attachment_id": "${exampleIds.attachment}", "portfolio_id": "${exampleIds.portfolio}", "is_complete": true, "unique_id": "xp-statement-2026-08-holdings", "reference_time": "2026-08-31"}]}`,
        }),
        responseExample: `{
  "requests": [
    {
      "id": "${exampleIds.positionParseRequest}",
      "status": "created",
      "attachment_id": "${exampleIds.attachment}",
      "account_id": "${exampleIds.account}",
      "portfolio_id": "${exampleIds.portfolio}",
      "unique_id": "xp-statement-2026-08-holdings",
      "reference_time": "2026-08-31T03:00:00+00:00",
      "is_complete": true,
      "created": "2026-09-01T10:04:12+00:00"
    }
  ],
  "message": "Position Parse Request successfully created."
}`,
      },
      {
        id: "list-position-parse-requests",
        legacyId: "list-parse-requests",
        method: "GET",
        path: "/position-parse-request",
        summary: "List Position Parse Requests",
        description: [
          "Returns the scoped account's holdings requests. Blotter requests are listed by their own route, so there is no type filter: the path is the type.",
        ],
        queryParams: parseRequestListParams,
        requestExample: curl({
          method: "GET",
          path: "/position-parse-request?status=partial&include_fields=results",
          scopeType: "account",
        }),
        responseExample: `{
  "requests": [
    {
      "id": "${exampleIds.positionParseRequest}",
      "status": "partial",
      "attachment_id": "${exampleIds.attachment}",
      "account_id": "${exampleIds.account}",
      "portfolio_id": "${exampleIds.portfolio}",
      "unique_id": "xp-statement-2026-08-holdings",
      "reference_time": "2026-08-31T03:00:00+00:00",
      "is_complete": false,
      "created": "2026-09-01T10:04:12+00:00",
      "results": [
        { "id": "b2d4f6a8c0e2b4d6f8a0c2e4b6d8f0a2c4e6", "outcome": "success", "row_count": 10 },
        { "id": "c4e6a8c0e2b4d6f8a0c2e4b6d8f0a2c4e6a8", "outcome": "rejected", "row_count": 2 }
      ],
      "reasons": [
        {
          "code": "completeness-not-applied",
          "message": "The file was not applied as a complete statement, so positions it does not list were left as they were."
        }
      ]
    }
  ],
  "cursor": ""
}`,
      },
      {
        id: "get-position-parse-request",
        legacyId: "get-parse-request",
        method: "GET",
        path: "/position-parse-request/{request_id}",
        summary: "Get a Position Parse Request",
        description: [
          "Returns one holdings request by id. Accepts the same `include_fields`. A request belonging to another account answers `not-found`. A blotter request's id is refused before any lookup runs: an id carries its own resource prefix, so a Trade Parse Request id here answers `invalid-type` on `path['request_id']`, not `not-found`.",
        ],
        queryParams: [parseRequestResultsFieldParam],
        requestExample: curl({
          method: "GET",
          path: `/position-parse-request/${exampleIds.positionParseRequest}?include_fields=results`,
          scopeType: "account",
        }),
      },
      {
        id: "get-position-parse-request-result",
        legacyId: "get-parse-request-result",
        method: "GET",
        path: "/position-parse-request/{request_id}/result",
        summary: "Get Position Parse Request Rows",
        description: [
          "Returns the holdings request's parsed rows, split into `success_rows` and `rejected_rows` as described above. A success row carries the `snapshot_id` it created; a rejected row carries its `reasons`.",
          "Answers only once the request is terminal; a request still `created` or `processing` is refused with `invalid-value` on `path['request_id']`.",
        ],
        requestExample: curl({
          method: "GET",
          path: `/position-parse-request/${exampleIds.positionParseRequest}/result`,
          scopeType: "account",
        }),
        responseExample: `{
  "result": {
    "success_rows": [
      {
        "row_number": 1,
        "raw": {"Date": "", "Ticker": "PETR4", "Quantity": "50", "Price": "37,50"},
        "snapshot_id": "${exampleIds.positionSnapshot}"
      }
    ],
    "rejected_rows": [
      {
        "row_number": 7,
        "raw": {"Date": "", "Ticker": "MXN/USD 04/23/25 Curncy", "Quantity": "10", "Price": "17,92"},
        "reasons": [
          {
            "code": "instrument-unresolvable",
            "message": "The provided identifier could not be resolved to an instrument.",
            "parameter": "symbol"
          }
        ]
      }
    ]
  }
}`,
      },
      {
        id: "list-position-parse-request-events",
        legacyId: "list-parse-request-events",
        method: "GET",
        path: "/position-parse-request/event",
        summary: "List Position Parse Request Events",
        description: [
          "Returns the scoped account's holdings-request events — one snapshot per state change. See Events for the envelope shape.",
          "Event types are finer than statuses: `registered`, `resolved`, and `sent` each show as status `processing` and mark the file being read, its instruments being resolved, and its rows being written.",
        ],
        queryParams: [...paginationParams],
        requestExample: curl({
          method: "GET",
          path: "/position-parse-request/event?limit=10",
          scopeType: "account",
        }),
      },
      {
        id: "create-trade-parse-requests",
        method: "POST",
        path: "/trade-parse-request",
        summary: "Create Trade Parse Requests",
        description: [
          "Creates blotter requests in the scoped account and starts each one. Every item names an attachment at status `success` and the portfolio its trades belong to. The route fixes what the rows become, a blotter dates its own rows, and a blotter never declares completeness, so the body carries none of `type`, `reference_time`, or `is_complete` — sending any of them is refused with `invalid-structure`, like any unknown key.",
          "The optional `unique_id` is the duplicate guard: a value already recorded for the account's blotter requests is refused with `already-exists`, and one repeated inside a batch with `invalid-value`. When it is omitted the platform generates one, and it comes back in the response like any other field — a caller that lost its response reads the request back with `GET /trade-parse-request?attachment_id=…` instead of guessing what was assigned.",
        ],
        bodyParams: [
          {
            name: "requests",
            type: "array",
            required: true,
            description: "1 to 100 request objects.",
          },
          {
            name: "requests[].attachment_id",
            type: "string",
            required: true,
            description: "Attachment to parse. Must be at status `success`.",
          },
          {
            name: "requests[].portfolio_id",
            type: "string",
            required: true,
            description: "Portfolio every trade of this request is written to.",
          },
          {
            name: "requests[].unique_id",
            type: "string",
            description: "Duplicate guard, unique per account among blotter requests, 1 to 128 characters.",
          },
        ],
        requestExample: curl({
          method: "POST",
          path: "/trade-parse-request",
          scopeType: "account",
          body: `{"requests": [{"attachment_id": "${exampleIds.attachment}", "portfolio_id": "${exampleIds.portfolio}", "unique_id": "xp-statement-2026-08-trades"}]}`,
        }),
        responseExample: `{
  "requests": [
    {
      "id": "${exampleIds.tradeParseRequest}",
      "status": "created",
      "attachment_id": "${exampleIds.attachment}",
      "account_id": "${exampleIds.account}",
      "portfolio_id": "${exampleIds.portfolio}",
      "unique_id": "xp-statement-2026-08-trades",
      "created": "2026-08-14T10:04:12+00:00"
    }
  ],
  "message": "Trade Parse Request successfully created."
}`,
      },
      {
        id: "list-trade-parse-requests",
        method: "GET",
        path: "/trade-parse-request",
        summary: "List Trade Parse Requests",
        description: [
          "Returns the scoped account's blotter requests. Holdings requests are listed by their own route, so there is no type filter: the path is the type.",
        ],
        queryParams: parseRequestListParams,
        requestExample: curl({
          method: "GET",
          path: "/trade-parse-request?status=partial&include_fields=results",
          scopeType: "account",
        }),
        responseExample: `{
  "requests": [
    {
      "id": "${exampleIds.tradeParseRequest}",
      "status": "partial",
      "attachment_id": "${exampleIds.attachment}",
      "account_id": "${exampleIds.account}",
      "portfolio_id": "${exampleIds.portfolio}",
      "unique_id": "xp-statement-2026-08-trades",
      "created": "2026-08-14T10:04:12+00:00",
      "results": [
        { "id": "e3fe1c8af23e9f317ffc16ad5a430cb7d226", "outcome": "success", "row_count": 10 },
        { "id": "e3fef40639b34ed02919cec888effa3a041f", "outcome": "rejected", "row_count": 2 }
      ],
      "reasons": []
    }
  ],
  "cursor": ""
}`,
      },
      {
        id: "get-trade-parse-request",
        method: "GET",
        path: "/trade-parse-request/{request_id}",
        summary: "Get a Trade Parse Request",
        description: [
          "Returns one blotter request by id. Accepts the same `include_fields`. A request belonging to another account answers `not-found`. A holdings request's id is refused before any lookup runs: an id carries its own resource prefix, so a Position Parse Request id here answers `invalid-type` on `path['request_id']`, not `not-found`.",
        ],
        queryParams: [parseRequestResultsFieldParam],
        requestExample: curl({
          method: "GET",
          path: `/trade-parse-request/${exampleIds.tradeParseRequest}?include_fields=results`,
          scopeType: "account",
        }),
      },
      {
        id: "get-trade-parse-request-result",
        method: "GET",
        path: "/trade-parse-request/{request_id}/result",
        summary: "Get Trade Parse Request Rows",
        description: [
          "Returns the blotter request's parsed rows, split into `success_rows` and `rejected_rows` as described above. A success row carries the `trade_id` it created; a rejected row carries its `reasons`.",
          "Answers only once the request is terminal; a request still `created` or `processing` is refused with `invalid-value` on `path['request_id']`.",
        ],
        requestExample: curl({
          method: "GET",
          path: `/trade-parse-request/${exampleIds.tradeParseRequest}/result`,
          scopeType: "account",
        }),
        responseExample: `{
  "result": {
    "success_rows": [
      {
        "row_number": 1,
        "raw": {"Date": "10/08/2026", "Ticker": "PETR4", "Side": "Buy", "Quantity": "100", "Price": "38,41"},
        "trade_id": "${exampleIds.externalTrade}"
      }
    ],
    "rejected_rows": [
      {
        "row_number": 7,
        "raw": {"Date": "11/08/2026", "Ticker": "MXN/USD 04/23/25 Curncy", "Side": "Sell", "Quantity": "10", "Price": "17,92"},
        "reasons": [
          {
            "code": "instrument-unresolvable",
            "message": "The provided identifier could not be resolved to an instrument.",
            "parameter": "symbol"
          }
        ]
      }
    ]
  }
}`,
      },
      {
        id: "list-trade-parse-request-events",
        method: "GET",
        path: "/trade-parse-request/event",
        summary: "List Trade Parse Request Events",
        description: [
          "Returns the scoped account's blotter-request events — one snapshot per state change. See Events for the envelope shape.",
          "Event types are finer than statuses: `registered`, `resolved`, and `sent` each show as status `processing` and mark the file being read, its instruments being resolved, and its rows being written.",
        ],
        queryParams: [...paginationParams],
        requestExample: curl({
          method: "GET",
          path: "/trade-parse-request/event?limit=10",
          scopeType: "account",
        }),
      },
    ],
  },
  {
    id: "position-snapshots",
    title: "Position Snapshots",
    description: [
      "A Position Snapshot declares what a portfolio actually held: this quantity of this instrument, at this instant. It exists because a blotter is only as complete as the documents that survive — when the record of a purchase is lost, the sale that remains reads as a short. A snapshot states the truth, and the platform adjusts to it.",
      "It adjusts at the trade level. Declaring a snapshot makes the platform book a compensating external trade for the difference between the declared quantity and what the recorded history implies, so every quantity still arrives as a trade and the audit trail stays whole. A difference of zero books nothing.",
      "A snapshot speaks only about the instrument it names, and only about externally held positions. Declare a quantity of 0 to erase a position the customer knows does not really exist — zero is a real declaration of flatness, not an empty one.",
      "The declared quantity is signed: a negative `symbol_amount` declares a short. This is the one place a quantity carries its direction in its sign rather than in a `side` — a trade records a flow, a snapshot records a state.",
      "There is no update. A snapshot is a dated statement, and a different statement is a different snapshot: cancel the wrong one and declare again. Position snapshot routes require account scope and the tracker product.",
      "The optional `average_price_amount` / `average_price_scale` pair is the cost basis for the declared quantity. Without it, the adjustment trade books with `price_amount` and `price_scale` both `null` — the one case where an External Trade carries no price — because the platform never assumes a price on your behalf.",
      "A snapshot a Position Parse Request creates carries the same shape: `unique_id` is `parse-request/{request_id}/{row_number}`, and `tags` carries `attachment/{attachment_id}` and `parse-request/{request_id}` — the shape to filter on to reconcile which snapshots one parse produced.",
    ],
    fields: [
      { name: "id", type: "string", description: "Unique snapshot identifier." },
      {
        name: "status",
        type: "string",
        description: "One of `created`, `processing`, `success`, `canceled`, or `failed`.",
      },
      { name: "portfolio_id", type: "string", description: "Portfolio the snapshot belongs to." },
      { name: "account_id", type: "string", description: "Account the snapshot belongs to." },
      { name: "instrument_id", type: "string", description: "Identifier of the declared instrument." },
      {
        name: "instrument_type",
        type: "string",
        description:
          "One of `debt`, `equity`, or `option`: the instrument types a snapshot can declare today. Derived from `instrument_id` when the snapshot is recorded, never sent by the caller. A fund instrument id is refused `not-found`, until that class becomes bookable. A currency instrument id is checked only against the ledger's own USD and BRL ids: one of those two is refused `instrument-type-not-bookable`; any other currency instrument id, including a real seeded currency pair, is refused `not-found` instead, since it is never checked against the catalog.",
      },
      {
        name: "symbol",
        type: "string",
        description:
          "Market symbol, resolved from the instrument when the snapshot is recorded and kept as it was then; never sent by the caller.",
      },
      {
        name: "symbol_amount",
        type: "integer",
        description: "Declared quantity, signed: negative declares a short, 0 declares flat.",
      },
      { name: "symbol_scale", type: "integer", description: "Decimal places of symbol_amount, 0 to 18." },
      {
        name: "average_price_amount",
        type: "integer",
        description: "Cost basis per unit for the declared quantity, when given.",
      },
      {
        name: "average_price_scale",
        type: "integer",
        description: "Decimal places of average_price_amount, 0 to 18.",
      },
      {
        name: "currency",
        type: "string",
        description: "Currency the instrument is denominated in, resolved from the instrument; never sent by the caller.",
      },
      {
        name: "reference_time",
        type: "string",
        description:
          "The instant the snapshot declares, in ISO 8601. A bare `YYYY-MM-DD` sent by the caller is read as midnight in Brasília time and returned as that full instant, never the bare date sent — significant here because `reference_time` is part of what makes a snapshot unique for its account, portfolio, and instrument (see `position-snapshot-already-declared` under Create Position Snapshots below), so a caller who sends a bare date and later its literal UTC midnight instant creates two distinct snapshots instead of hitting that collision.",
      },
      {
        name: "unique_id",
        type: "string",
        description: "Client-supplied duplicate guard, empty when not given.",
      },
      { name: "tags", type: "list of strings", description: "Free-form labels attached to the snapshot." },
      { name: "created", type: "string", description: "Creation timestamp in ISO 8601, UTC." },
    ],
    endpoints: [
      {
        id: "create-position-snapshots",
        method: "POST",
        path: "/position-snapshot",
        summary: "Create Position Snapshots",
        description: [
          "Declares snapshots in the scoped account. The instrument travels as `instrument_id` alone; its type, symbol, and currency are resolved from the catalog.",
          "Every entry in one batch must name the same `portfolio_id` — a batch is a statement about one portfolio, and a mixed batch is refused. Declaring several portfolios is one request each.",
          "An instant that already carries a standing snapshot for the same portfolio and instrument is refused with `position-snapshot-already-declared`: two declared truths for one instant contradict each other, so cancel the standing one first. The same instant repeated inside one batch is refused too.",
        ],
        bodyParams: [
          {
            name: "snapshots",
            type: "array",
            required: true,
            description: "1 to 100 snapshot objects.",
          },
          {
            name: "snapshots[].portfolio_id",
            type: "string",
            required: true,
            description: "Portfolio the snapshot belongs to. The same for every entry in a batch.",
          },
          {
            name: "snapshots[].instrument_id",
            type: "string",
            required: true,
            description: "Identifier of the declared instrument.",
          },
          {
            name: "snapshots[].symbol_amount",
            type: "integer",
            required: true,
            description: "Declared quantity as a signed integer.",
          },
          {
            name: "snapshots[].symbol_scale",
            type: "integer",
            required: true,
            description: "Decimal places of symbol_amount, 0 to 18.",
          },
          {
            name: "snapshots[].reference_time",
            type: "string",
            required: true,
            description:
              "A date `YYYY-MM-DD` or a timestamp with a UTC offset. A bare date means midnight in Brasília time.",
          },
          {
            name: "snapshots[].average_price_amount",
            type: "integer",
            description: "Cost basis per unit for the declared quantity. Supplied together with `average_price_scale`, or not at all.",
          },
          {
            name: "snapshots[].average_price_scale",
            type: "integer",
            description: "Decimal places of average_price_amount, 0 to 18.",
          },
          {
            name: "snapshots[].unique_id",
            type: "string",
            description:
              "Duplicate guard, unique per account, 1 to 64 characters. A value already recorded is refused with `already-exists`, and a value repeated in one batch with `invalid-value`. To confirm a replay, read the earlier write back with `GET /position-snapshot?unique_id=…` and check its `status`.",
          },
          {
            name: "snapshots[].tags",
            type: "list of strings",
            description: "Free-form labels to attach.",
          },
        ],
        requestExample: curl({
          method: "POST",
          path: "/position-snapshot",
          scopeType: "account",
          body: `{"snapshots": [{"portfolio_id": "${exampleIds.portfolio}", "instrument_id": "${exampleIds.equityInstrument}", "symbol_amount": 50, "symbol_scale": 0, "reference_time": "2026-08-01", "average_price_amount": 3750, "average_price_scale": 2, "unique_id": "xp-snapshot-2026-08-01"}]}`,
        }),
        responseExample: `{
  "snapshots": [
    {
      "id": "${exampleIds.positionSnapshot}",
      "status": "created",
      "portfolio_id": "${exampleIds.portfolio}",
      "account_id": "${exampleIds.account}",
      "instrument_id": "${exampleIds.equityInstrument}",
      "instrument_type": "equity",
      "symbol": "PETR4",
      "symbol_amount": 50,
      "symbol_scale": 0,
      "average_price_amount": 3750,
      "average_price_scale": 2,
      "currency": "BRL",
      "reference_time": "2026-08-01T03:00:00+00:00",
      "unique_id": "xp-snapshot-2026-08-01",
      "tags": [],
      "created": "2026-08-14T10:06:02+00:00"
    }
  ],
  "message": "Position Snapshot successfully created."
}`,
      },
      {
        id: "list-position-snapshots",
        method: "GET",
        path: "/position-snapshot",
        summary: "List Position Snapshots",
        description: ["Returns the scoped account's position snapshots."],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated snapshot ids, up to 100.",
          },
          {
            name: "portfolio_id",
            type: "string",
            description: "Filter by portfolio.",
          },
          {
            name: "status",
            type: "list of strings",
            description: "Comma-separated statuses: `created`, `processing`, `success`, `canceled`, or `failed`.",
          },
          {
            name: "unique_id",
            type: "string",
            description: "Find the snapshot carrying this duplicate guard.",
          },
          {
            name: "order_by",
            type: "string",
            description: "`reference_time`, `-reference_time`, `created`, or `-created`.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: `/position-snapshot?portfolio_id=${exampleIds.portfolio}`,
          scopeType: "account",
        }),
      },
      {
        id: "cancel-position-snapshots",
        method: "PATCH",
        path: "/position-snapshot",
        summary: "Cancel Position Snapshots",
        description: [
          "Cancels standing snapshots by id, up to 30 in one call — `status` accepts `canceled` only. Only a snapshot at status `success` can be canceled; one still `created` or `processing` is refused with `invalid-value`.",
          "The `canceled` event is not the end of it: cancellation is asynchronous, and the snapshot moves to `voided` once its adjustment trade is reversed — see Position Snapshot Events. The next snapshot's adjustment is recomputed over the interval the cancellation merged.",
        ],
        bodyParams: [
          {
            name: "snapshots",
            type: "array",
            required: true,
            description: "1 to 30 snapshot objects.",
          },
          {
            name: "snapshots[].id",
            type: "string",
            required: true,
            description: "Snapshot to cancel.",
          },
          {
            name: "snapshots[].status",
            type: "string",
            required: true,
            description: "Must be `canceled`.",
          },
        ],
        requestExample: curl({
          method: "PATCH",
          path: "/position-snapshot",
          scopeType: "account",
          body: `{"snapshots": [{"id": "${exampleIds.positionSnapshot}", "status": "canceled"}]}`,
        }),
      },
      {
        id: "list-position-snapshot-events",
        method: "GET",
        path: "/position-snapshot/event",
        summary: "List Position Snapshot Events",
        description: [
          "Returns the event history of the scoped account's position snapshots — one event per state change. See Events for the envelope shape.",
          "`success` is not terminal here: recording or canceling a trade dated inside a settled snapshot's interval legitimately reopens it while its adjustment is reissued. The declared quantity never changes — only the adjustment does.",
          "Canceling is asynchronous too: a `canceled` event lands first, then `voided` once the adjustment trade is reversed. Both keep the snapshot's `status` at `canceled`.",
          "A `failed` event carries reason `adjustment-trade-failed`.",
        ],
        queryParams: [
          ...paginationParams,
          {
            name: "ids",
            type: "list of strings",
            description: "Comma-separated event ids.",
          },
          {
            name: "snapshot_ids",
            type: "list of strings",
            description: "Comma-separated snapshot ids, limiting events to those snapshots.",
          },
          {
            name: "types",
            type: "string",
            description:
              "Filter by event type: `created`, `booked`, `success`, `canceled`, `voided`, or `failed`.",
          },
          {
            name: "after",
            type: "string",
            description: "Return events created after this date or timestamp.",
          },
          {
            name: "before",
            type: "string",
            description: "Return events created before this date or timestamp.",
          },
        ],
        requestExample: curl({
          method: "GET",
          path: "/position-snapshot/event?limit=10",
          scopeType: "account",
        }),
      },
    ],
  },
];
